import React from "react";
import { userPaymentsApi, AddRegistrationTrancheRequest } from "api";
import {
  PaziaButton,
  PaziaSelect,
  PaziaFormButton,
  PaziaInput,
  PaziaDateSelect,
} from "components/atoms";
import { PaziaModal } from "components/molecules";
import { REGISTRATION_TRANCHE_PAYMENT_STATUSES_OPTIONS, RegistrationTrancheStatus } from "consts";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { getApiErrorMessage, useToaster } from "utils";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";

interface AddTrancheModalProps {
  onCloseRequest: () => void;
  isOpen: boolean;
  userId: number;
  courseId: number;
}

export const AddTrancheModal = ({ onCloseRequest, isOpen, userId, courseId }: AddTrancheModalProps) => {
  const { showError, showSuccess } = useToaster();

  const onSubmit = async (data: FormValues) => {
    try {
      const requestBody: AddRegistrationTrancheRequest = {
        name: data.name,
        amount: data.amount,
        dueDate: data.dueDate,
        status: data.status as RegistrationTrancheStatus,
      };

      await userPaymentsApi.createTranche(userId, courseId, requestBody);
      showSuccess("Transza została utworzona");
    } catch (error) {
      console.error(error);
      showError(getApiErrorMessage(error, "Wystąpił błąd podczas tworzenia transzy"));
    } finally {
      onCloseRequest();
    }
  };

  return (
    <PaziaModal id="add-tranche-modal" onCloseRequest={onCloseRequest} isOpen={isOpen} size="2xl">
      <ModalPadding>
        <ModalHeader>Dodaj nową transzę</ModalHeader>
        <AddTrancheForm onSubmit={onSubmit} onCancel={onCloseRequest} />
      </ModalPadding>
    </PaziaModal>
  );
};

const AddTrancheForm = ({
  onSubmit,
  onCancel,
}: {
  onSubmit: (data: FormValues) => Promise<void>;
  onCancel: () => void;
}) => {
  const schema = yup
    .object({
      name: yup.string().required("Nazwa transzy jest wymagana"),
      amount: yup.number().required("Kwota jest wymagana").min(0, "Kwota musi być większa od 0"),
      dueDate: yup.date().required("Data płatności jest wymagana"),
      status: yup.number().required("Status jest wymagany"),
    })
    .required();

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      amount: 0,
      dueDate: new Date(),
      status: 1, // NOT_PAID
    },
  });
  const { handleSubmit } = methods;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <PaziaInput name="name" label="Nazwa transzy" placeholder="Wprowadź nazwę transzy" />

        <PaziaInput name="amount" label="Kwota (PLN)" type="number" step=".01" placeholder="0.00" />

        <PaziaDateSelect name="dueDate" label="Data płatności" />

        <PaziaSelect
          name="status"
          label="Status transzy"
          placeholder="Wybierz status"
          options={REGISTRATION_TRANCHE_PAYMENT_STATUSES_OPTIONS}
        />

        <div className="mt-4 flex justify-end gap-2">
          <PaziaButton btnType="button" type="tertiary" onClick={onCancel} title="Anuluj" />
          <PaziaFormButton type="success" title="Dodaj transzę" />
        </div>
      </form>
    </FormProvider>
  );
};

type FormValues = {
  name: string;
  amount: number;
  dueDate: Date;
  status: number;
};
